import { MouseEventHandler, type RefObject } from 'react';
import type { PAGE_POSITIONS } from 'types';
import type { ArticleCard, ArticleCardImage } from 'types/capi/shared';

import Image from '~/components/Image';
import Tracked from '~/components/Tracked';
import ArticleCardInteractionEvent from '~/events/ArticleCardInteractionEvent';
import useArticleCardImpression from '~/hooks/useArticleCardImpression';
import useRootData from '~/hooks/useRootData';
import useUser from '~/hooks/useUser';
import type { TrackedButtonClickName } from '~/services/gtm.client';

import Metadata, {
  MetadataSize,
  type MetadataTheme,
  type TitleLines
} from './Metadata';

type Ratio = '16:9' | '4:5';

type Variant =
  | '16/9-text-over'
  | '16/9-text-under'
  | '4/5-text-over'
  | '4/5-text-under';

type Props = {
  className?: string;
  hideCta?: boolean;
  item: ArticleCard;
  mdRatio?: Ratio;
  modulePosition?: number;
  moduleType: string;
  name: TrackedButtonClickName;
  onClick?: MouseEventHandler<HTMLAnchorElement>;
  options?: {
    compact?: boolean;
    expandedMetadata?: boolean;
    showSection?: boolean;
    theme?: MetadataTheme;
    titleLines?: TitleLines;
    titleSize?: MetadataSize;
  };
  pagePosition: PAGE_POSITIONS | `TEMP_${string}`;
  position: number;
  variant: Variant;
};

const VARIANT_RATIOS: Record<Variant, Ratio> = {
  '16/9-text-over': '16:9',
  '16/9-text-under': '16:9',
  '4/5-text-over': '4:5',
  '4/5-text-under': '4:5'
};

const ASPECTS: Record<Ratio, string> = {
  '16:9': 'aspect-[16/9]',
  '4:5': 'aspect-[4/5]'
};

const MD_ASPECTS: Record<Ratio, string> = {
  '16:9': 'md:aspect-[16/9]',
  '4:5': 'md:aspect-[4/5]'
};

const PORTRAIT_REQUEST_HEIGHT = 672 as const;

function aspect(ratio: Ratio, desktopRatio: Ratio) {
  return desktopRatio === ratio
    ? ASPECTS[ratio]
    : `${ASPECTS[ratio]} ${MD_ASPECTS[desktopRatio]}`;
}

function imageRequest(ratio: Ratio, width: 672 | 900) {
  return ratio === '4:5'
    ? { height: PORTRAIT_REQUEST_HEIGHT, retainRatio: true }
    : { width };
}

function pickImage(
  images: ArticleCardImage[],
  ratio: ArticleCardImage['ratio']
): ArticleCardImage | undefined {
  return images.find((i) => i.ratio === ratio) ?? images[0];
}

export default function ArticleCard({
  className,
  hideCta,
  item,
  mdRatio,
  modulePosition,
  moduleType,
  name,
  onClick,
  options,
  pagePosition,
  position,
  variant
}: Props) {
  const { theme } = options ?? {};
  const { isBot } = useRootData();
  const user = useUser();
  const impressionRef = useArticleCardImpression<HTMLDivElement>({
    articleId: item.id,
    modulePosition,
    moduleType,
    position
  });

  const isTextOver =
    variant === '16/9-text-over' || variant === '4/5-text-over';
  const ratio = VARIANT_RATIOS[variant];
  const desktopRatio = mdRatio ?? ratio;
  const image = pickImage(item.images, ratio);
  const desktopImage = pickImage(item.images, desktopRatio);
  const resolvedTheme: MetadataTheme = theme ?? (isTextOver ? 'dark' : 'light');

  const card = {
    desktopImage,
    desktopRatio,
    hideCta,
    image,
    impressionRef,
    item,
    options: { ...options, theme: resolvedTheme },
    ratio
  };

  return (
    <Tracked.InternalLink
      to={item.link.path}
      variant="none"
      pagePosition={pagePosition}
      name={name}
      onClick={(event) => {
        new ArticleCardInteractionEvent({
          articleId: item.id,
          gesture: 'click',
          guestId: user.guestId,
          isBot,
          modulePosition,
          moduleType,
          position
        }).send();

        if (onClick) onClick(event);
      }}
      className={className}
    >
      {isTextOver ? <TextOverImage {...card} /> : <TextUnderImage {...card} />}
    </Tracked.InternalLink>
  );
}

type CardProps = {
  desktopImage?: ArticleCardImage;
  desktopRatio: Ratio;
  hideCta?: boolean;
  image?: ArticleCardImage;
  impressionRef: RefObject<HTMLDivElement>;
  item: ArticleCard;
  options: Props['options'];
  ratio: Ratio;
};

function TextUnderImage({
  desktopImage,
  desktopRatio,
  hideCta,
  image,
  impressionRef,
  item,
  options,
  ratio
}: CardProps) {
  const {
    compact,
    expandedMetadata,
    showSection,
    theme = 'light',
    titleLines,
    titleSize = 'sm'
  } = options ?? {};

  return (
    <div
      className={`flex flex-col gap-2 text-start ${
        compact ? '' : 'min-w-[285px]'
      }`}
      ref={impressionRef}
    >
      {image ? (
        <Image
          src={image.url}
          className={`w-full ${aspect(ratio, desktopRatio)} object-cover`}
          alt={image.alt}
          quality={100}
          loading="lazy"
          responsiveOptions={{
            md: { ...imageRequest(desktopRatio, 900), src: desktopImage?.url },
            xs: imageRequest(ratio, 672)
          }}
        />
      ) : null}
      <Metadata
        item={item}
        hideCta={hideCta}
        showSection={showSection}
        size={titleSize}
        theme={theme}
        titleLines={titleLines}
        expanded={expandedMetadata}
      />
    </div>
  );
}

function TextOverImage({
  desktopImage,
  desktopRatio,
  hideCta,
  image,
  impressionRef,
  item,
  options,
  ratio
}: CardProps) {
  const {
    compact,
    expandedMetadata,
    showSection,
    theme = 'light',
    titleLines,
    titleSize = 'md'
  } = options ?? {};

  return (
    <div
      className={`relative flex flex-col items-center justify-end ${
        compact ? 'w-full' : 'size-full min-h-[356px] md:min-h-[450px]'
      } ${aspect(ratio, desktopRatio)}`}
      ref={impressionRef}
    >
      {image ? (
        <Image
          src={image.url}
          className="absolute z-10 block size-full object-cover"
          alt={image.alt}
          loading="lazy"
          responsiveOptions={{
            md: { height: 672, retainRatio: true, src: desktopImage?.url },
            xs: { height: 672, retainRatio: true }
          }}
          progressiveLoadSettings={{ highResQuality: 100, lowResQuality: 50 }}
          retainRatio
        />
      ) : null}
      <div className="absolute inset-0 z-20 bg-gradient-to-b from-transparent to-black opacity-70" />
      <div className="z-30 flex size-full flex-col items-start justify-end gap-1 px-6 pb-6 text-white md:px-8 md:pb-8">
        <Metadata
          expanded={expandedMetadata}
          hideCta={hideCta}
          isTextOverImage
          item={item}
          showSection={showSection}
          size={titleSize}
          theme={theme}
          titleLines={titleLines}
        />
      </div>
    </div>
  );
}
